import { useQuery } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { getSiteCopy } from "@/lib/api";
import { useBrief } from "@/context/BriefContext";
import { track } from "@/lib/analytics";
import { OperatorPacket } from "@/components/OperatorPacket";
import { Seo } from "@/components/Seo";
import { PAGE_SEO, faqLd } from "@/lib/seo";
import { ArrowRight } from "lucide-react";
import { amp } from "@/lib/amp";

const fade = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

// Copper middot separator
const Dot = () => (
  <span className="text-[color:var(--copper)] px-1.5" aria-hidden="true">&middot;</span>
);

// Cormorant italic copper ampersand — the signature flourish.
const Amp = () => <span className="exec-amp">&amp;</span>;

// Accent the final word of a headline in Cormorant italic copper.
function HeadlineAccent({ text }) {
  const t = (text || "").trim();
  const words = t.split(" ");
  if (words.length < 2) return <>{t}</>;
  const last = words.pop();
  return (
    <>
      {words.join(" ")}{" "}
      <span className="exec-serif italic font-medium text-[color:var(--copper)] tracking-normal">
        {last}
      </span>
    </>
  );
}

export default function Home() {
  const navigate = useNavigate();
  const { openBrief } = useBrief();
  const { data: copy = {} } = useQuery({ queryKey: ["site-copy"], queryFn: getSiteCopy });

  // Segment cards. Copy comes from site_copy; links route to the dedicated
  // landing pages. The government card is always present.
  const doors = [
    { key: 1, label: copy.door_1_label, text: copy.door_1_text, link: "/for/entrepreneurs", testid: "home-door-entrepreneurs-card" },
    { key: 2, label: copy.door_2_label, text: copy.door_2_text, link: "/for/founders", testid: "home-door-founders-card" },
    { key: 3, label: copy.door_3_label, text: copy.door_3_text, link: "/for/enterprises", testid: "home-door-enterprises-card" },
    { key: 4, label: copy.door_4_label, text: copy.door_4_text, link: "/for/advisors", testid: "home-door-advisors-card" },
    { key: 5, label: copy.door_5_label, text: copy.door_5_text, link: "/for/nonprofits", testid: "home-door-nonprofits-card" },
    {
      key: 6,
      label: "For government \u0026 public sector",
      text: "Municipal innovation, workforce programs, and AI readiness for agencies under real scrutiny. Women- and minority-owned, procurement-ready. We meet you before the RFP, not after.",
      link: "/public-sector",
      testid: "home-door-public-sector-card",
    },
  ];

  const commitments = [
    { n: "01", t: copy.commitment_1_title, b: copy.commitment_1_body },
    { n: "02", t: copy.commitment_2_title, b: copy.commitment_2_body },
    { n: "03", t: copy.commitment_3_title, b: copy.commitment_3_body },
  ];

  const proofStats = [
    { outcome: "Concept to developer-ready MVP", time: "12 weeks, no venture funding." },
    { outcome: "Technical ambiguity to funder-ready", time: "60 days." },
    { outcome: "Vision to a defined customer and growth roadmap", time: "Under 5 weeks." },
  ];

  const goStartSprint = (location) => {
    track("door_click", { target: "sprint", location });
    navigate("/services/founder-tech-clarity-sprint");
  };

  return (
    <div className="exec-scope min-h-screen" data-testid="home-page">
      <Seo
        {...PAGE_SEO.home}
        jsonLd={faqLd([
          {
            q: "What is a Sprint?",
            a: "A Sprint is a focused two-to-four-week engagement on one decision or build. We find what your team can do, take on the gap, and leave you with the work done and a memo on what to do next.",
          },
          {
            q: "Who does Copper & Vine work with?",
            a: "Founders, entrepreneurs, enterprises, nonprofits and institutions, and the public sector.",
          },
          {
            q: "When was Copper & Vine founded?",
            a: "Copper & Vine was founded in 2003 and has launched more than 400 products and platforms for clients from Fortune 500 to federal.",
          },
        ])}
      />

      {/* Hero — text-led, gallery-grade */}
      <section data-testid="home-hero" className="exec-container pt-24 md:pt-32 pb-16">
        <motion.div {...fade} className="max-w-[920px]">
          <p className="exec-eyebrow mb-9">Executive Technology Advisory <Dot /> Since 2003</p>
          <h1
            data-testid="home-hero-headline"
            className="font-extralight text-[clamp(40px,5.6vw,76px)] leading-[1.08] tracking-[0.005em]"
          >
            <HeadlineAccent text={copy.hero_headline || "Build, raise, and scale with confidence."} />
          </h1>
          <p
            data-testid="home-hero-deck"
            className="exec-serif font-normal text-[clamp(20px,2.2vw,25px)] leading-[1.5] text-[color:var(--lead-ink)] max-w-[46ch] mt-9"
          >
            {copy.hero_subhead}
          </p>
          <div className="flex flex-wrap gap-4 mt-12">
            <button data-testid="hero-primary-cta-button" className="exec-btn exec-btn-primary" onClick={() => goStartSprint("hero")}>
              {copy.hero_primary_cta || "Start a Sprint"}
            </button>
            <button
              data-testid="hero-secondary-cta-button"
              className="exec-btn exec-btn-secondary"
              onClick={() => {
                track("door_click", { target: "brief_request", location: "hero" });
                openBrief("general");
              }}
            >
              {copy.hero_secondary_cta || "Request the brief"}
            </button>
          </div>
          {/* Trust line */}
          <div className="mt-14">
            <div className="exec-rule" />
            <p
              data-testid="home-hero-trustline"
              className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--grey)] font-normal mt-5 flex flex-wrap items-center"
            >
              400+ products since 2003 <Dot /> Salesforce <Dot /> Apple <Dot /> federal agencies <Dot /> the White House
            </p>
          </div>
        </motion.div>
      </section>

      {/* How we start */}
      <section className="exec-container pb-20" data-testid="home-how-we-start">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[760px]">
          <p className="exec-eyebrow">How we start</p>
          <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-5">
            Most clients start with a Sprint.
          </h2>
          <p className="exec-serif text-[clamp(17px,1.9vw,21px)] leading-[1.55] text-[color:var(--lead-ink)] mt-6">
            A focused two-to-four-week engagement on one decision or build: a UI you haven’t
            validated, a compliance certification, a feature your team can’t staff. We find what
            your team can do, take on the gap, and leave you with the work done and a memo on what
            to do next.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mt-9">
            <button
              data-testid="how-we-start-sprint-button"
              className="exec-btn exec-btn-primary"
              onClick={() => goStartSprint("how_we_start")}
            >
              Start a Sprint
            </button>
            <Link
              to="/services"
              data-testid="how-we-start-ladder-link"
              onClick={() => track("door_click", { target: "/services", location: "how_we_start" })}
              className="exec-link"
            >
              See the ladder <ArrowRight size={14} />
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Proof band */}
      <section className="exec-container pb-20">
        <p className="exec-eyebrow" data-testid="home-proof-label">Proof</p>
        <h2
          data-testid="home-proof-stats"
          className="font-light text-[clamp(22px,2.6vw,34px)] leading-[1.2] mt-5 lg:whitespace-nowrap"
        >
          400+ products and platforms <Dot /> 20+ years <Dot /> Fortune 500 to federal
        </h2>
        <p
          data-testid="home-proof-band"
          className="exec-serif text-[clamp(16px,1.7vw,19px)] leading-[1.55] text-[color:var(--lead-ink)] max-w-3xl mt-5"
        >
          {copy.proof_band}
        </p>

        <div className="exec-rule mt-10" />
        <div data-testid="home-proof-numbers" className="py-10">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-10">
            {proofStats.map((s) => (
              <div key={s.outcome}>
                <p className="exec-serif text-[clamp(20px,2vw,27px)] leading-[1.25] text-[color:var(--ink)]">{s.outcome}</p>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[color:var(--copper-deep)] mt-3 font-normal">{s.time}</p>
              </div>
            ))}
          </div>
          <Link
            to="/work"
            data-testid="home-see-the-work"
            onClick={() => track("door_click", { target: "/work", location: "proof_numbers" })}
            className="exec-link mt-9"
          >
            See the work <ArrowRight size={14} />
          </Link>
        </div>
        <div className="exec-rule" />
      </section>

      {/* Where you stand — segment doors */}
      <section className="exec-container py-20">
        <p className="exec-eyebrow">Where you stand</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-8">
          {doors.filter((d) => d.label).map((d) => (
            <motion.button
              key={d.key}
              {...fade}
              data-testid={d.testid}
              onClick={() => {
                track("door_click", { target: d.link, door: d.key, location: "home_doors" });
                navigate(d.link || "/services");
              }}
              className="exec-card group text-left p-8 flex flex-col min-h-[230px]"
            >
              <span className="exec-eyebrow">{amp(d.label)}</span>
              <p className="exec-serif text-[17px] leading-[1.5] text-[color:var(--ink)] mt-4 flex-1">{d.text}</p>
              <span className="exec-link mt-6">
                Enter
                <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </motion.button>
          ))}
        </div>
      </section>

      {/* The independence — light pull-quote (no longer a dark band) */}
      <section className="exec-container py-20">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[920px]">
          <p className="exec-eyebrow">The independence</p>
          <p className="exec-serif text-[clamp(26px,3.4vw,44px)] leading-[1.3] text-[color:var(--ink)] mt-7">
            {copy.differentiator}
          </p>
        </motion.div>
      </section>

      {/* Who does the work */}
      <section className="exec-container py-20" data-testid="home-who-does-the-work">
        <div className="exec-rule mb-16" />
        <motion.div {...fade} className="max-w-[760px]">
          <p className="exec-eyebrow">The team</p>
          <h2 className="font-light text-[clamp(28px,3.6vw,42px)] leading-[1.14] mt-5">
            Who does the work.
          </h2>
          <p className="exec-serif text-[clamp(17px,1.9vw,21px)] leading-[1.55] text-[color:var(--lead-ink)] mt-6">
            Copper <Amp /> Vine assembles a senior team for each engagement, the level of operator who
            usually sits in a CTO or executive seat. Fractional CTOs and CIOs, compliance architects,
            product strategists, and go-to-market leads. You get the caliber, scoped to the work.
          </p>
        </motion.div>
      </section>

      {/* Commitments — hairline grid */}
      <section className="exec-container pb-20">
        <p className="exec-eyebrow">Three commitments</p>
        <div data-testid="home-commitments" className="grid grid-cols-1 md:grid-cols-3 gap-px mt-8 bg-[color:var(--rule)] p-px">
          {commitments.map((c) => (
            <motion.div key={c.n} {...fade} className="bg-[color:var(--paper)] p-8">
              <p className="exec-serif italic text-[30px] text-[color:var(--copper)] leading-none">{c.n}</p>
              <div className="exec-rule my-5" />
              <h3 className="text-[12px] uppercase tracking-[0.2em] font-normal text-[color:var(--ink)]">{c.t}</h3>
              <p className="exec-serif text-[16px] leading-[1.5] text-[color:var(--lead-ink)] mt-3">{c.b}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Operator Packet */}
      <section className="exec-container pb-20">
        <OperatorPacket title={copy.packet_title || "The Operating Room"} body={copy.packet_body || ""} />
      </section>

      {/* Executive Production — single sanctioned availability signal */}
      <section className="exec-container pb-24">
        <div
          data-testid="home-executive-production-banner"
          className="border-t border-b border-[color:var(--rule)] py-9 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
        >
          <div className="max-w-2xl">
            <p className="exec-eyebrow">Beyond advisory</p>
            <p className="exec-serif text-[17px] sm:text-[19px] leading-[1.5] text-[color:var(--lead-ink)] mt-3">
              Your AI can write the plan. We are the team that runs it.
              Conferences, go-to-market, and live activation.
            </p>
          </div>
          <a
            data-testid="home-executive-production-cta"
            href="/executive-production"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("door_click", { target: "/executive-production", location: "home_banner" })}
            className="exec-link whitespace-nowrap shrink-0"
          >
            See Executive Production <ArrowRight size={14} />
          </a>
        </div>
      </section>
    </div>
  );
}
